import { assertPence, type Pence } from './money.js';

/** Rewards Club rules (CLAUDE.md §4). Change only with Saif's say-so. */
export const POINTS_PER_POUND_CARD = 5;
export const POINTS_PER_POUND_CREDIT = 10;
export const SIGNUP_BONUS_POINTS = 100;

export interface RewardTier {
  id: 'side' | 'main';
  name: string;
  points: number;
}

export const REWARD_TIERS: readonly RewardTier[] = [
  { id: 'side', name: 'Free side or dessert', points: 250 },
  { id: 'main', name: 'Free Regular main', points: 1000 },
];

/** The Rewards Club numbers as a settings object (editable in the admin from stage 2; the constants above are the defaults). */
export interface RewardsRules {
  pointsPerPoundCard: number;
  pointsPerPoundCredit: number;
  signupBonus: number;
  tiers: RewardTier[];
}
export const DEFAULT_REWARDS: RewardsRules = { pointsPerPoundCard: POINTS_PER_POUND_CARD, pointsPerPoundCredit: POINTS_PER_POUND_CREDIT, signupBonus: SIGNUP_BONUS_POINTS, tiers: REWARD_TIERS.map((t) => ({ ...t })) };

export class RewardsRulesError extends Error {
  override name = 'RewardsRulesError';
}
/** Check numbers Saif typed in the admin before they are saved: whole, non-negative, both tiers present and sensible. */
export function validateRewardsRules(r: RewardsRules): RewardsRules {
  const whole = (v: unknown, label: string, max: number) => {
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > max) throw new RewardsRulesError(`${label} must be a whole number between 0 and ${max.toLocaleString('en-GB')}`);
    return v;
  };
  const tiers = Array.isArray(r.tiers) ? r.tiers : [];
  const side = tiers.find((t) => t.id === 'side'), main = tiers.find((t) => t.id === 'main');
  if (!side || !main) throw new RewardsRulesError('Both rewards (free side and free main) must be set');
  const out: RewardsRules = {
    pointsPerPoundCard: whole(r.pointsPerPoundCard, 'Points per £1 by card', 100),
    pointsPerPoundCredit: whole(r.pointsPerPoundCredit, 'Points per £1 with credit', 100),
    signupBonus: whole(r.signupBonus, 'Sign-up bonus', 10000),
    tiers: [
      { id: 'side', name: String(side.name || 'Free side or dessert').slice(0, 60), points: whole(side.points, 'Free side points', 100000) },
      { id: 'main', name: String(main.name || 'Free Regular main').slice(0, 60), points: whole(main.points, 'Free main points', 100000) },
    ],
  };
  if (out.tiers[0]!.points < 1 || out.tiers[1]!.points < 1) throw new RewardsRulesError('A reward must cost at least 1 point');
  if (out.tiers[1]!.points <= out.tiers[0]!.points) throw new RewardsRulesError('The free main must cost more points than the free side');
  return out;
}

/**
 * Points earned for the money actually paid on an order.
 * 5 points per £1 paid by card (or Apple/Google Pay), 10 per £1 paid with store credit,
 * worked out to the penny and rounded down. Free items earn nothing because they cost nothing.
 * `multiplier` is for future double-points hours (off at launch).
 */
export function pointsEarned(paid: { creditPence: Pence; cardPence: Pence }, multiplier = 1, rules: RewardsRules = DEFAULT_REWARDS): number {
  assertPence(paid.creditPence, 'creditPence');
  assertPence(paid.cardPence, 'cardPence');
  if (!Number.isInteger(multiplier) || multiplier < 1) throw new RangeError('multiplier must be a whole number >= 1');
  return Math.floor(((paid.creditPence * rules.pointsPerPoundCredit + paid.cardPence * rules.pointsPerPoundCard) / 100) * multiplier);
}

export function rewardTier(id: RewardTier['id'], tiers: readonly RewardTier[] = REWARD_TIERS): RewardTier {
  const t = tiers.find((r) => r.id === id);
  if (!t) throw new RangeError(`Unknown reward "${id}"`);
  return t;
}

/** Rewards the customer can afford with their free (unreserved) points. */
export function affordableRewards(freePoints: number, tiers: readonly RewardTier[] = REWARD_TIERS): RewardTier[] {
  return tiers.filter((r) => freePoints >= r.points);
}

/** Can they reserve `rewardId` on top of what's already reserved in the bag? */
export function canReserveReward(pointsBalance: number, alreadyReserved: number, rewardId: RewardTier['id'], tiers: readonly RewardTier[] = REWARD_TIERS): boolean {
  return pointsBalance - alreadyReserved >= rewardTier(rewardId, tiers).points;
}
